/**
 * Audit Sessions Table Component
 *
 * Displays audit sessions in a table format with status, progress, and management options.
 * Handles table column definitions, cell rendering, and business logic for audit session display.
 */

import { DataList, type TableColumn, Pagination, ProgressBar, TimestampDisplay } from '@core/components';
import { AuditSessionStatusBadge } from '@internal-audit/components/AuditSessionStatusBadge';
import type { FlexibleAuditSession } from '@internal-audit/domain/graphql-types';
import { Text, ButtonType, Delete, IconButton, OverlayMode, IconSize, Edit, Eye } from '@soteria/design-system-ve';
import { useState, useMemo, useEffect } from 'react';

import * as styles from './AuditSessionsTable.css';
import { DeleteAuditSessionDialog } from './DeleteAuditSessionDialog';

interface AuditSessionsTableProps {
  sessions: FlexibleAuditSession[];
  isLoading: boolean;
  onViewSession: (session: FlexibleAuditSession) => void;
  onDeleteSession?: (session: FlexibleAuditSession) => void;
}

export function AuditSessionsTable({ sessions, isLoading, onViewSession, onDeleteSession }: AuditSessionsTableProps) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [sessionToDelete, setSessionToDelete] = useState<FlexibleAuditSession | null>(null);
  const [currentPage, setCurrentPage] = useState(1);

  // Pagination constants
  const ITEMS_PER_PAGE = 10;

  // Calculate pagination values
  const totalPages = Math.ceil(sessions.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;

  // Get current page data
  const paginatedSessions = useMemo(() => sessions.slice(startIndex, endIndex), [sessions, startIndex, endIndex]);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Reset to first page when sessions change or when current page becomes invalid
  useEffect(() => {
    if (sessions.length === 0) {
      // Reset to page 1 when no sessions
      setCurrentPage(1);
    } else if (currentPage > totalPages && totalPages > 0) {
      // Reset to last valid page when current page is out of bounds
      setCurrentPage(totalPages);
    }
  }, [sessions.length, currentPage, totalPages]);

  // Handle delete button click
  const handleDeleteClick = (session: FlexibleAuditSession) => {
    setSessionToDelete(session);
    setDeleteDialogOpen(true);
  };

  // Handle successful deletion
  const handleDeleteSuccess = () => {
    if (sessionToDelete && onDeleteSession) {
      onDeleteSession(sessionToDelete);
    }

    setDeleteDialogOpen(false);
    setSessionToDelete(null);
  };

  // Handle delete dialog close
  const handleDeleteClose = () => {
    setDeleteDialogOpen(false);
    setSessionToDelete(null);
  };

  const getProgressPercentage = (session: FlexibleAuditSession): number => {
    const totalItems = session.itemsCount || 0;
    const completedItems = session.completedCount || 0;
    const skippedItems = session.skippedCount || 0;

    if (totalItems === 0) return 0;

    // Both completed and skipped items are considered "done" for progress
    return Math.round(((completedItems + skippedItems) / totalItems) * 100);
  };

  const renderProgress = (session: FlexibleAuditSession) => {
    const percentage = getProgressPercentage(session);

    return (
      <ProgressBar
        animated
        aria-label={`Progress: ${percentage}% complete`}
        color="primary"
        size="large"
        text={`${percentage}%`}
        textAlign="right"
        textPosition="after"
        value={Math.max(percentage, 0)}
      />
    );
  };

  const columns: TableColumn<FlexibleAuditSession>[] = [
    {
      key: 'name',
      header: 'Session Name',
      render: (session: FlexibleAuditSession) => (
        <div>
          <Text className={styles.sessionName}>{session.name}</Text>
          {session.description && <Text className={styles.sessionDescription}>{session.description}</Text>}
        </div>
      ),
      width: '30%',
    },
    {
      key: 'createdBy',
      header: 'Created By',
      render: (session: FlexibleAuditSession) => <Text>{session.createdBy}</Text>,
      width: '15%',
    },
    {
      key: 'createdAt',
      header: 'Created At',
      render: (session: FlexibleAuditSession) => <TimestampDisplay timestamp={session.createdAt} />,
      width: '15%',
    },
    {
      key: 'status',
      header: 'Status',
      render: (session: FlexibleAuditSession) => <AuditSessionStatusBadge status={session.status} />,
      width: '10%',
    },
    {
      key: 'progress',
      header: 'Progress',
      render: renderProgress,
      width: '20%',
    },
    {
      key: 'items',
      header: 'Items',
      render: (session: FlexibleAuditSession) => {
        const totalItems = session.itemsCount || 0;

        return <Text>{totalItems}</Text>;
      },
      width: '10%',
    },
  ];

  // Add delete button column if onDeleteSession is provided
  // This will appear as part of the actions group on the right side
  if (onDeleteSession) {
    columns.push({
      key: 'actions',
      header: '',
      render: (session: FlexibleAuditSession) => (
        <div className={styles.actionsContainer}>
          <IconButton
            aria-label={session.status === 'completed' ? `View ${session.name}` : `Edit ${session.name}`}
            buttonType={ButtonType.Secondary}
            icon={session.status === 'completed' ? Eye : Edit}
            iconSize={IconSize.XSmall}
            overlayMode={OverlayMode.Solid}
            title={session.status === 'completed' ? `View ${session.name}` : `Audit ${session.name}`}
            onClick={() => onViewSession(session)}
          />
          <IconButton
            aria-label={`Delete ${session.name}`}
            buttonType={ButtonType.Danger}
            icon={Delete}
            iconSize={IconSize.XSmall}
            overlayMode={OverlayMode.Solid}
            title={`Delete ${session.name}`}
            onClick={() => handleDeleteClick(session)}
          />
        </div>
      ),
      width: '14%',
    });
  }

  return (
    <>
      <DataList columns={columns} data={paginatedSessions} dataTypeName="audit session" isLoading={isLoading} />

      {/* Pagination Controls */}
      <Pagination currentPage={currentPage} disabled={isLoading} totalPages={totalPages} onPageChange={handlePageChange} />

      {/* Custom Delete Dialog */}
      <DeleteAuditSessionDialog auditSession={sessionToDelete} isOpen={deleteDialogOpen} onClose={handleDeleteClose} onSuccess={handleDeleteSuccess} />
    </>
  );
}
